/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Tomáš Burian (burian@cadwork.cz)
 *
 */

import ViewControls from "App/view/controls/view_controls";
import RenderDocument from "App/render_document";
import View from "App/view/view";
import Camera from "App/view/cameras/camera";
import {vec3} from "gl-matrix";
import {degToRad} from "App/math";
import {logInfo} from "App/debug";
import BoundingBox from "App/utils/bounding_box";
import Messenger from "App/messenger";
import {EViewControls, EViewControlsFamily} from "js_interface/src/modules/view_controls/scene_view_controls_enums";

enum FallDownResolverState {
    GO_FORWARD,
    FALL_DOWN_CANDIDATE_DETECTED,
    DO_FALL_DOWN_IN_NEXT_RESOLVE
}

class FallDownResolver {
    private static MAX_GAP_SIZE: number = 0.3; // in meters
    private static FALL_DOWN_DETECTION_HEIGHT: number = 0.4; // in meters

    private state: FallDownResolverState = FallDownResolverState.GO_FORWARD;
    private cameraPosition: vec3 = vec3.create(); // because of garbage collection
    private gapStartCameraPosition: vec3 = vec3.create(); // because of garbage collection
    private gapTestCameraPosition: vec3 = vec3.create(); // because of garbage collection
    private cameraTarget: vec3 = vec3.create(); // because of garbage collection
    private viewDirection: vec3 = vec3.create();
    private distancefromSurface: number = 0; // distance from surface including the avatar height

    constructor() {
        //
    }

    public initialize(startingPosition: vec3) {
        this.distancefromSurface = startingPosition[1];
    }

    public setAvatarHeight(oldHeight: number, newHeight: number) {
        this.distancefromSurface -= oldHeight;
        this.distancefromSurface += newHeight;
    }

    public resolve(aPickedPointsDown: vec3[], aCamera: Camera, aViewDirection: vec3, avatarHeight: number) {

        if (aPickedPointsDown.length === 0 || !aPickedPointsDown[0]) { // this should not happen, but if so, just stop moving
            return;
        }

        // detect fall down to gap
        if ((this.distancefromSurface - aPickedPointsDown[0][1]) > FallDownResolver.FALL_DOWN_DETECTION_HEIGHT) {

            if (this.state === FallDownResolverState.GO_FORWARD) {
                // store the entering point for the gap
                vec3.copy(this.gapStartCameraPosition, aCamera.getCameraPosition());
                vec3.copy(this.gapTestCameraPosition, aCamera.getCameraPosition());
                this.state = FallDownResolverState.FALL_DOWN_CANDIDATE_DETECTED;
            }
            else {
                vec3.copy(this.gapTestCameraPosition, aCamera.getCameraPosition());
            }

            if (this.getDistanceFromGapFirstDetection() > FallDownResolver.MAX_GAP_SIZE) {
                // schedule fall down now, just because the avatar step could be so that in next turn st. will be picked
                if (this.state === FallDownResolverState.FALL_DOWN_CANDIDATE_DETECTED) {
                    this.state = FallDownResolverState.DO_FALL_DOWN_IN_NEXT_RESOLVE;
                    // keep the height for the last time
                }
                else {
                    // finally do the fall down
                    this.distancefromSurface = aPickedPointsDown[0][1] + avatarHeight;
                    this.state = FallDownResolverState.GO_FORWARD;
                }
            }
            else { // keep the height and not allow to fall down to the gap
            }
        }
        else {
            this.distancefromSurface = aPickedPointsDown[0][1] + avatarHeight;
            this.state = FallDownResolverState.GO_FORWARD;
        }

        this.moveForward(aPickedPointsDown[0], aCamera, aViewDirection, avatarHeight);
    }

    private moveForward(pointDown: vec3, aCamera: Camera, aViewDir: vec3, avatarHeight: number) {

        vec3.copy(this.cameraPosition, pointDown);
        this.cameraPosition[1] = this.distancefromSurface;

        // compute looking direction
        vec3.normalize(this.viewDirection, aViewDir);

        vec3.add(this.cameraTarget, this.cameraPosition, this.viewDirection);

        aCamera.setCameraPositionAndTarget(this.cameraPosition, this.cameraTarget);
    }

    private getDistanceFromGapFirstDetection(): number {
        let distance = vec3.distance(this.gapStartCameraPosition, this.gapTestCameraPosition);
        return distance;
    }
}

export default
class WalkControls extends ViewControls {

    public static SPEED_STEPS_MIN: number = 0;
    public static SPEED_STEPS_MAX: number = 11;
    public static TOUCH_WALK_TIME_DELAY: number = 500; // after this time camera starts move forward if touch coords are the same or in defined radius
    public static TOUCH_WALK_TOUCH_RADIUS: number = 20;  // limit to handle touch as the same as the starting coordinates
    public static VIEW_ANGLE_MIN: number = 45;
    public static VIEW_ANGLE_MAX: number = 90;
    public static MOUSE_SENSITIVITY_MIN: number = 1;
    public static MOUSE_SENSITIVITY_MAX: number = 5;
    public static AVATAR_HEIGHT_MIN: number = 0.5;
    public static AVATAR_HEIGHT_MAX: number = 2;

    private fallDownResolver: FallDownResolver = new FallDownResolver();
    private _collisionCameraDown: Camera;
    private avatarHeight: number = 1.5;
    private avatarWidth: number = 0.5;
    private moveFlagMap: any[];
    private PICK_DOWN_NEAR_VALUE: number = 0.01;
    private CDWK_FP_MODE_SPEED: number;
    private speedMax: number = 0;
    private speedValues: number[];
    private _stepFactor: number = 0.1;
    private prevMouseWheelTime: number = 0;
    private _touchCoords: { x: number; y: number };
    private _autoMovement: {
        timeoutSpeed: number;
        last: number;
        lastSpeed: number;
        distance: number;
        speedStep: number;
        maxSpeedStep: number;
        timeout: number;
        direction: vec3;
        distanceLast: number
    };
    private _downDir: vec3 = vec3.fromValues(0, -1, 0);
    private _touchDoubleTapTimeout: number = null;
    private speed: number = 0;

    constructor(aDocument: RenderDocument, aView: View) {
        super(aDocument, aView);

        this._family = EViewControlsFamily.WALK;
        this._allowLogDepth = true;
        this._useLogDepth = true;

        this._camera = new Camera(this._view);
        this._initCameraParams();

        // down collision camera and its settings
        this._collisionCameraDown = this._camera.clone();
        this._collisionCameraDown.setCameraUpVector(vec3.fromValues(0, 0, 1));
        // this is needed because when the camera is high above the underlying geometry, then the depth buffer resolution is not sufficient to pick exact point
        this._collisionCameraDown.setFieldOfView(0.0001);

        this.moveFlagMap = [];
        this.moveFlagMap[this.position1] = 0;
        this.moveFlagMap[this.position2] = 0;
        this.moveFlagMap[this.position3] = 0;
        this.moveFlagMap[this.target1] = 0;
        this.moveFlagMap[this.target2] = 0;
        this.moveFlagMap[this.target3] = 0;
        this.moveFlagMap[this.moveSpeed] = 0;

        this.CDWK_FP_MODE_SPEED = 0.5;    // m/s
        this.CDWK_FP_MODE_NUM_STEPS = 11;	    // Number of steps (mouse wheel scrolls) to get to max speed.
        this.CDWK_FP_MODE_MIN_MAX_SPEED = 40.0;   // Minimal maximal speed (to be able to move fast even in small models)
        this.CDWK_FP_MODE_MAX_SPEED_RATIO = 0.1;    // Maximal speed ratio to compute max speed

        /* Speed values table: when mode bbox is large, the upper part is used as well
         * This way it enables walking through a house in a large landscape.
         */
        this.speedValues = [this.CDWK_FP_MODE_NUM_STEPS];
        this.speedValues[0] = 0.1;
        this.speedValues[1] = 0.3;
        this.speedValues[2] = 0.5;
        this.speedValues[3] = 1.0;
        this.speedValues[4] = 2.0;
        this.speedValues[5] = 4.0;
        this.speedValues[6] = 8.0;
        this.speedValues[7] = 16.0;
        this.speedValues[8] = 32.0;
        this.speedValues[9] = 64.0;
        this.speedValues[10] = 100.0;
        this.step = 0;

        this.setSpeed(this.step);

        // forward camera and its settings derived form camera with opening angle set (compared to down collision camera)
        this._collisionCameraForward = this._camera.clone();
        this._collisionCameraForward.forceNearValue(0.01); // 0.01 is empiric value
        // this is a 10% behind the maximum walking step which is 100.0
        this._collisionCameraForward.forceFarValue(this.speedValues[10] * this._stepFactor * 1.1);
        // Touch events
        this._touchCoords = {x: 0, y: 0};

        this._autoMovementAllowed = false;
        this._autoMovement = {
            maxSpeedStep: 10,
            timeout: 3000, // ms
            timeoutSpeed: 1000, // ms
            last: 0,
            lastSpeed: 0,
            speedStep: -1,
            direction: vec3.create(), // normalized
            distance: 0.0001,
            distanceLast: 0.0001
        };

        // aView.getControls().checkAndInitLogarithmicDepthBuffer();
        this.checkAndInitLogarithmicDepthBuffer();

        this.emitSpeed();

        // extend the pgc from view controls
        this._pgc.fwdCamPos = vec3.create();
        this._pgc.fwdCamTgt = vec3.create();
        this._pgc.newPos = vec3.create();
        this._pgc.newTgt = vec3.create();
        this._pgc.upDir = vec3.create();
        this._pgc.posDir = vec3.create();
        this._pgc.downPos = vec3.create();
        this._pgc.downTgt = vec3.create();
        this._pgc.moveDir = vec3.create();
        this._pgc.viewDir = vec3.create();
        this._pgc.obstacleDir = vec3.create();
        this._pgc.obstacleDir2D = vec3.create();
        this._pgc.collisionTgt = vec3.create();
    }

    public static getSpeedLimits(): {min: number, max: number} {
        return {
            min: WalkControls.SPEED_STEPS_MIN,
            max: WalkControls.SPEED_STEPS_MAX
        };
    }

    public static getViewAngleLimits(): {min: number, max: number} {
        return {
            min: WalkControls.VIEW_ANGLE_MIN,
            max: WalkControls.VIEW_ANGLE_MAX
        };
    }

    public static getMouseSensitivityLimits(): {min: number, max: number} {
        return {
            min: WalkControls.MOUSE_SENSITIVITY_MIN,
            max: WalkControls.MOUSE_SENSITIVITY_MAX
        };
    }

    public static getAvatarHeightLimits(): {min: number, max: number} {
        return {
            min: WalkControls.AVATAR_HEIGHT_MIN,
            max: WalkControls.AVATAR_HEIGHT_MAX
        };
    }

    public _initCameraParams() {
        super._initCameraParams();

        this.openingAngle = 60.0;

        this._camera.setFieldOfView(degToRad(this.openingAngle));

        // this is for non logarithmic rendering, for logarithmic rendering the near plane is effectively zero
        this._camera.forceNearValue(0.1);
    }

    public init() {
        super.init();

        // one render pass to ensure the camera is ready
        this._view.render();

        this._initCameraForWalking();
        // this.oldTexturesResolutionLimitRT = TextureNode.texturesResolutionSwitchLimitRT;
        // Renderer.setTexturesResolutionSwitchLimitRT(256.0);
    }

    // reset mode specialities
    public destroy() {
        // Renderer.setTexturesResolutionSwitchLimitRT(this.oldTexturesResolutionLimitRT);
    }

    public onBoundingBoxUpdated(aBoundingBox: BoundingBox, aBoundingBoxForNPC: BoundingBox) {
        super.onBoundingBoxUpdated(aBoundingBox, aBoundingBoxForNPC);

        this._collisionCameraDown.onBoundingBoxUpdated(aBoundingBox, aBoundingBoxForNPC);
        this._collisionCameraForward.onBoundingBoxUpdated(aBoundingBox, aBoundingBoxForNPC);

        this.speedMax = this.computeMaxSpeed(aBoundingBox);
    }

    public getControlsType(): EViewControls {
        return EViewControls.WALK;
    }

    public onMousePress(x: number, y: number, buttonState: number): boolean {
        if (!this.isEnabled()) {
            return false;
        }

        if (this._pointerLockUsed) {
            if (buttonState & 1) { // left mb
                // this.moveFlagMap[this.goUp] = true;
                this.moveFlagMap[this.position3] = 1;
            }
            if (buttonState & 2) { // right mb
                // this.moveFlagMap[this.goDown] = true;
                this.moveFlagMap[this.position3] = -1;
            }
        }
        this._mouseState.set(0, 0, x, y, x, y, buttonState, true);

        return true;
    }

    public onMouseRelease(x: number, y: number, buttonState: number, prevButton: number): boolean {
        if (!this.isEnabled()) {
            return false;
        }

        if (prevButton & 1) { // left mb
            // this.moveFlagMap[this.goUp] = false;
            this.moveFlagMap[this.position3] = 0;
        }
        if (prevButton & 2) { // right mb
            // this.moveFlagMap[this.goDown] = false;
            this.moveFlagMap[this.position3] = 0;
        }

        this._mouseState.set(0, 0, x, y, this._mouseState.pressX, this._mouseState.pressY, buttonState, false);

        return true;
    }

    public onDrag(x: number, y: number, delta: number, buttonState: number): boolean {
        if (!this.isEnabled()) {
            return false;
        }

        let dx = x - this._mouseState.lastX;
        let dy = y - this._mouseState.lastY;

        this._mouseState.isMoving = true;

        if (buttonState & 1 || buttonState & 2) { // left and right
            this.rotateCameraTarget(this._view.horizontalPixelsToRad(dx), this._view.verticalPixelsToRad(dy));
        }

        this._mouseState.dx = dx;
        this._mouseState.dy = dy;

        this._mouseState.lastX = x;
        this._mouseState.lastY = y;

        this._view.viewpointChanged();

        return true;
    }

    public onWheel(x: number, y: number, delta: number, buttonState: number): boolean {
        return true;
    }

    public computeMaxSpeed(aBoundingBox: BoundingBox): number {
        // select max and divide
        let max = aBoundingBox.getDiameter(); // the diameter is the max here
        let speed = max * this.CDWK_FP_MODE_MAX_SPEED_RATIO;

        // check limits for small models
        speed = Math.max(speed, this.CDWK_FP_MODE_MIN_MAX_SPEED); // min max speed correction

        // logInfo('max speed: '+ speed);
        return speed;
    }

    public emitSpeed() {
        let sp = (this.step + 1) / this.CDWK_FP_MODE_NUM_STEPS * 100.0;

        Messenger.fireCDWKEvent(this._sceneAPI.getTargetTag(), "cwx3d_walkingSpeedChanged", {
            speedPercent: sp,
            value: this.step
        });
    }

    public onMouseIn(x: number, y: number, buttonState: number): boolean {
        if (!this.isEnabled()) {
            return false;
        }

        this._mouseState.set(0, 0, x, y, this._mouseState.pressX, this._mouseState.pressY, 0, false);

        return true;
    }

    public onMouseOut(x: number, y: number, buttonState: number): boolean {
        if (!this.isEnabled()) {
            return false;
        }

        this._mouseState.set(0, 0, x, y, this._mouseState.pressX, this._mouseState.pressY, 0, false);

        return true;
    }

    public onDoubleClick(x: number, y: number): boolean {
        return true;
    }

    public onTouchStart(x: number, y: number, numTouches: number): boolean {
        if (!this.isEnabled()) {
            return false;
        }

        super.onTouchStart(x, y, numTouches);

        // set start coords by default (in case there is no touch move)
        this._touchCoords.x = x;
        this._touchCoords.y = y;

        this._touchDoubleTapTimeout = window.setTimeout(() => {
            let deltaX = Math.abs(this._touchCoords.x - x);
            let deltaY = Math.abs(this._touchCoords.y - y);

            if (deltaX <= WalkControls.TOUCH_WALK_TOUCH_RADIUS &&
                deltaY <= WalkControls.TOUCH_WALK_TOUCH_RADIUS) {
                // that.moveFlagMap[that.goForward] = true;
                this.moveFlagMap[this.position1] = 1;
            }
        }, WalkControls.TOUCH_WALK_TIME_DELAY);

        return true;
    }

    public onTouchEnd(x: number, y: number, remainingTouch: number, aDraggedDuringTouch: boolean): boolean {
        if (!this.isEnabled()) {
            return false;
        }

        super.onTouchEnd(x, y, remainingTouch, aDraggedDuringTouch);

        // this.moveFlagMap[this.goForward] = false;
        this.moveFlagMap[this.position1] = 0;
        window.clearTimeout(this._touchDoubleTapTimeout);

        return true;
    }

    public onTouchMove(x: number, y: number, numTouches: number, delta: number): boolean {
        if (!this.isEnabled()) {
            return false;
        }

        super.onTouchMove(x, y, numTouches, delta);

        this._touchCoords.x = x;
        this._touchCoords.y = y;

        return true;
    }

    public onKeyDown(key: string) {
        if (!this.isEnabled()) {
            return false;
        }

        switch (key) {
            case  "Up": // up arr IE/Edge
            case  "ArrowUp": // up arr
            case  "W": // W
            case "w": // w
                if (!this._ctrlDown) {
                    // this.moveFlagMap[this.goForward] = true;
                    this.moveFlagMap[this.position1] = 1;
                }
                break;

            case "Down": // down arr IE/Edge
            case "ArrowDown": // down arr
            case  "S": // S
            case "s": // s
                if (!this._ctrlDown) {
                    // this.moveFlagMap[this.goBack] = true;
                    this.moveFlagMap[this.position1] = -1;
                }
                break;

            case  "Left": // left arr IE/Edge
            case  "ArrowLeft": // left arr
            case  "A": // A
            case  "a": // a
                if (!this._ctrlDown) {
                    // this.moveFlagMap[this.goLeft] = true;
                    this.moveFlagMap[this.position2] = -1;
                }
                break;

            case  "Right": // right arr IE/Edge
            case  "ArrowRight": // right arr
            case  "D": // D
            case "d": // d
                if (!this._ctrlDown) {
                    // this.moveFlagMap[this.goRight] = true;
                    this.moveFlagMap[this.position2] = 1;
                }
                break;

            case  "PageUp": // page up
                // this.moveFlagMap[this.goUp] = true;
                this.moveFlagMap[this.position3] = 1;
                break;

            case  "PageDown": // page down
                // this.moveFlagMap[this.goDown] = true;
                this.moveFlagMap[this.position3] = -1;
                break;

            case "Shift": // shift
                if (!this._shiftDown) {
                    if (this.step + 1 < this.CDWK_FP_MODE_NUM_STEPS) {
                        this.setSpeed(this.step + 1);
                    }
                    this._shiftDown = true;
                }
                break;

            case "Control": // ctrl
                this._ctrlDown = true;
                break;

            default:
                break;
        }
        // logInfo('wc key down ' + keyCode);
    }

    public onKeyUp(key: string) {
        if (!this.isEnabled()) {
            return false;
        }

        switch (key) {
            case  "Up": // up arr
            case  "ArrowUp": // up arr
            case  "W": // W
            case  "w": // w
                // this.moveFlagMap[this.goForward] = false;
                this.moveFlagMap[this.position1] = 0;
                break;

            case  "Down": // down arr
            case  "ArrowDown": // down arr
            case  "S": // S
            case  "s": // s
                // this.moveFlagMap[this.goBack] = false;
                this.moveFlagMap[this.position1] = 0;
                break;

            case  "Left": // left arr
            case  "ArrowLeft": // left arr
            case  "A": // A
            case  "a": // a
                // this.moveFlagMap[this.goLeft] = false;
                this.moveFlagMap[this.position2] = 0;
                break;

            case  "Right": // right arr
            case  "ArrowRight": // right arr
            case  "D": // D
            case  "d": // d
                // this.moveFlagMap[this.goRight] = false;
                this.moveFlagMap[this.position2] = 0;
                break;

            case  "PageUp": // page up
                // this.moveFlagMap[this.goUp] = false;
                this.moveFlagMap[this.position3] = 0;
                break;

            case  "PageDown": // page down
                // this.moveFlagMap[this.goDown] = false;
                this.moveFlagMap[this.position3] = 0;
                break;

            case "Shift": // shift
                if (this._shiftDown) {
                    if (this.step > 0) {
                        this.setSpeed(this.step - 1);
                    }
                    this._shiftDown = false;
                }
                break;

            case "Control": // ctrl
                this._ctrlDown = false;
                break;

            default:
                break;
        }
        // logInfo('wc key up ' + keyCode);
    }

    public onKeyPress(key: string) {
        if (!this.isEnabled()) {
            return false;
        }

        switch (key) {
            // cdwk
            /*
                    case  43: // + (incr. speed)
                        let evt = document.createEvent("Event");
                        evt.initEvent("mousewheel", true, true);
                        evt.wheelDelta = 120; // the 120 is default wheel value used in all major browsers?
                        this._view.getHTMLCanvas().dispatchEvent(evt);
                        break;
                    case  45: // - (decr. speed)
                        let evt = document.createEvent("Event");
                        evt.initEvent("mousewheel", true, true);
                        evt.wheelDelta = -120; // the -120 is default wheel value used in all major browsers?
                        this._view.getHTMLCanvas().dispatchEvent(evt);
                        break;
            */
            case "Enter": // ENTER
                // case 27: // ESC // problem to catch and used to cancel fullscreen mode
                // this._view.resetViewMode();
                // notify gui
                break;
            case "i": // i show debug log dialog
            case "d": // d show debug log dialog
                // toggleShowDebugConsole = !toggleShowDebugConsole;
                // activate(toggleShowDebugConsole);
                break;
            case "/":  // / show properties hud
                this._sceneAPI.getStateViewer().toggle();
                break;
            default:
                break;
        }
        // logInfo('wc key press ' + keyCode);
    }

    public tick(): void {
        super.tick();

        this.speed = this.speedValues[this.step];

        // accumulate positions and target vector changes to call camera update only once per move
        let change = false;
        vec3.copy(this._pgc.newPos, this._camera.getCameraPosition());
        vec3.copy(this._pgc.newTgt, this._camera.getCameraTarget());

        if (this._autoMovementAllowed) {
            let now = Date.now();
            let viewDir = this._camera.getCameraDirection();

            if (this._autoMovement.speedStep === -1) { // detect walking start

                if (this._autoMovement.last === 0) { // fisrt
                    this._autoMovement.last = now;
                    this._autoMovement.lastSpeed = now;
                } else if (now - this._autoMovement.last >= this._autoMovement.timeout) { // another cheks
                    this._autoMovement.last = now;
                    this._autoMovement.lastSpeed = now;

                    // logInfo(viewDir.toString());

                    let dotProduct = vec3.dot(viewDir, this._autoMovement.direction);
                    let angle = Math.acos(dotProduct);

                    // logInfo('dotProduct ' + dotProduct);
                    // logInfo('angle ' + angle);

                    if (dotProduct === 0 || dotProduct >= 1.0 || angle <= 0.174533) { // 10 des in radians
                        logInfo('START walking ');
                        this._autoMovement.speedStep = 0;
                    }
                    vec3.copy(this._autoMovement.direction, viewDir);
                }

            } else {
                // already walking

                // logInfo('already walking');

                if (now - this._autoMovement.lastSpeed >= this._autoMovement.timeoutSpeed) { // check for speed changes

                    logInfo('  d = ' + this._autoMovement.distance);

                    // tslint:disable-next-line:no-shadowed-variable
                    let change = this._autoMovement.distanceLast / this._autoMovement.distance;
                    let x = 1 - change;
                    if (x > 0 && x >= 0.25) {
                        this._autoMovement.speedStep++;
                    } else if (x < 0 && x <= -0.25) {
                        this._autoMovement.speedStep--;
                    }

                    logInfo('  ' + change + ':  x = ' + x + ' ss ' + this._autoMovement.speedStep);

                    if (this._autoMovement.speedStep > this._autoMovement.maxSpeedStep) {
                        this._autoMovement.speedStep = this._autoMovement.maxSpeedStep;
                    } else if (this._autoMovement.speedStep < 0) {
                        this._autoMovement.speedStep = 0;
                    }

                    this._autoMovement.lastSpeed = now;
                    this._autoMovement.distanceLast = this._autoMovement.distance;

                    // logInfo(this.speed);

                }
                // detect stop walking movement (look down)
                // define the up dir by hand, because it the camera one is changed in CameraVR
                vec3.set(this._pgc.down, 0, -1, 0);

                let dotProductDown = vec3.dot(this._downDir, viewDir);
                let angleDown = Math.acos(dotProductDown);

                // logInfo('upDir ' + this._camera.getCameraUpVectorRef().toString());
                // logInfo('viewDir ' + viewDir.toString());

                // logInfo('dotProductDown ' + dotProductDown);
                // logInfo('angleDown ' + angleDown);

                if (dotProductDown >= 1.0 || angleDown <= 0.349066) { // 20 des in radians (30 = 0.523599)
                    logInfo('STOP walking ');
                    this._autoMovement.speedStep = -1;
                }
                vec3.copy(this._autoMovement.direction, viewDir);

            }

            if (this._autoMovement.speedStep >= 0) {
                this.setSpeed(this._autoMovement.speedStep);
                this.moveFlagMap[this.goForward] = true;
            } else {
                this.moveFlagMap[this.goForward] = false;
            }
        }

        let step = this.speed * this._stepFactor;
        let position1 = Math.abs(this.gamepadFlagMap[this.position1]) > this._gamepadZero ? this.gamepadFlagMap[this.position1] : this.moveFlagMap[this.position1];
        let position2 = Math.abs(this.gamepadFlagMap[this.position2]) > this._gamepadZero ? this.gamepadFlagMap[this.position2] : this.moveFlagMap[this.position2];
        let position3 = Math.abs(this.gamepadFlagMap[this.position3]) > this._gamepadZero ? this.gamepadFlagMap[this.position3] : this.moveFlagMap[this.position3];

        if (Math.abs(position1) > this._gamepadZero) { // camera position forward / backward
            vec3.copy(this._pgc.viewDir, this._camera.getCameraDirection());

            vec3.scale(this._pgc.viewDir, this._pgc.viewDir, step);
            vec3.scale(this._pgc.viewDir, this._pgc.viewDir, Math.abs(position1));

            vec3.copy(this._pgc.posDir, this._pgc.viewDir);
            if (position1 < 0) {
                vec3.negate(this._pgc.posDir, this._pgc.viewDir);
            }

            vec3.add(this._pgc.newPos, this._pgc.newPos, this._pgc.posDir);
            vec3.add(this._pgc.newTgt, this._pgc.newPos, this._pgc.viewDir);
            change = true;
        }

        if (Math.abs(position2) > this._gamepadZero) { // camera position left / right
            vec3.copy(this._pgc.viewDir, this._camera.getCameraDirection());
            let upDir = this._camera.getCameraUpVectorRef();

            vec3.scale(this._pgc.viewDir, this._pgc.viewDir, step);
            vec3.scale(this._pgc.viewDir, this._pgc.viewDir, Math.abs(position2));

            if (position2 > 0) {
                vec3.cross(this._pgc.viewDir, this._pgc.viewDir, upDir);
            } else {
                vec3.cross(this._pgc.viewDir, upDir, this._pgc.viewDir);
            }

            vec3.add(this._pgc.newPos, this._pgc.newPos, this._pgc.viewDir);
            vec3.add(this._pgc.newTgt, this._pgc.newTgt, this._pgc.viewDir);
            change = true;
        }

        // camera position up / down // when walking this does not apply because camera is always in the height of avatar
        if (Math.abs(position3) > this._gamepadZero) {
            vec3.set(this._pgc.upDir, 0, 1, 0);
            vec3.scale(this._pgc.upDir, this._pgc.upDir, step);
            vec3.scale(this._pgc.upDir, this._pgc.upDir, Math.abs(position3));

            if (position1 < 0) {
                vec3.negate(this._pgc.posDir, this._pgc.upDir);
            }

            vec3.add(this._pgc.newPos, this._pgc.newPos, this._pgc.posDir);
            vec3.add(this._pgc.newTgt, this._pgc.newTgt, this._pgc.posDir);
            change = true;
        }

        if (change) {
            this.moveAndCollideCamera(this._pgc.newPos, this._pgc.newTgt);
            this._viewMode.scheduleRedraw();
        }
    }

    public pickForCameraCollisionForward(aNewPosition: vec3) {
        let curCamPos = this._camera.getCameraPosition();

        // compute camera move direction
        vec3.subtract(this._pgc.moveDir, aNewPosition, curCamPos);
        vec3.normalize(this._pgc.moveDir, this._pgc.moveDir);

        // let the target lies in the xz plane perpendicular to y axis where the current camera position lies in the plane
        this._pgc.moveDir[1] = 0.0;

        vec3.add(this._pgc.collisionTgt, curCamPos, this._pgc.moveDir);

        // the camera collision forward has the same parameters as the _camera used for rendering the scene in walking mode
        this._collisionCameraForward.setCameraPositionAndTarget(curCamPos, this._pgc.collisionTgt);

        let oldCamera = this._camera;
        this._camera = this._collisionCameraForward;

        let centerWindowPoint = this._view.getCenterWindowPoint();
        let points = this._document.renderForValues(this._view, {
            type: "cdwk_depth_custom_camera",
            readPosition: [
                centerWindowPoint[0],
                centerWindowPoint[1],
                centerWindowPoint[0],
                centerWindowPoint[1] - 5],
            requestsBufferRedraw: true
        });

        this._camera = oldCamera;

        return points;
    }

    public pickForInitCameraForWalking(aCamPosition: vec3) {
        vec3.copy(this._pgc.downPos, aCamPosition);
        vec3.add(this._pgc.downTgt, this._pgc.downPos, this._downDir);

        // set camera for picking down for walking mode inicialization
        this._camera.backup();
        this._camera.setCameraUpVector(vec3.fromValues(0, 0, 1));
        // this is needed because when the camera is high above the underlying geometry, then the depth buffer resolution is not sufficient to pick exact point
        this._camera.setFieldOfView(0.1);
        this._camera.forceNearValue(0.1);

        // do not use up vector [0,1,0] when camera is looking down, because the view vector direction 0,-1,0
        // causes that the model view matrix is computed strangely
        this._camera.setCameraPositionAndTarget(this._pgc.downPos, this._pgc.downTgt);

        let position = this._view.getPosition();
        let size = this._view.getSize();
        // we do not know whether the camera is close to some object of far away so we have to perform precise picking here to correct positioning the camera
        let pagePos = this._sceneAPI.getCanvas().getPageMousePositionFromPhysPosition([
            position[0] + (size[0] / 2.0),
            position[1] + (size[1] / 2.0)
        ]);
        let pos = this._sceneAPI.pickingModule.getPrecise3DPosition(pagePos[0], pagePos[1]);

        this._camera.restore();

        return pos;
    }

    public pickForCameraCollisionDown(aFwdCamPosition: vec3) {
        vec3.copy(this._pgc.downPos, aFwdCamPosition);
        vec3.add(this._pgc.downTgt, this._pgc.downPos, this._downDir);

        // causes that the model view matrix is computed strangely
        this._collisionCameraDown.setCameraPositionAndTarget(this._pgc.downPos, this._pgc.downTgt);
        this._collisionCameraDown.forceNearValue(0.1);
        this._collisionCameraDown.setFieldOfView(0.1);

        let oldCamera = this._camera;
        this._camera = this._collisionCameraDown;

        let centerWindowPoint = this._view.getCenterWindowPoint();
        let points = this._document.renderForValues(this._view, {
            type: "cdwk_depth_custom_camera",
            readPosition: [
                centerWindowPoint[0],
                centerWindowPoint[1]
            ],
            requestsBufferRedraw: true
        });

        this._camera = oldCamera;

        return points;
    }

    public moveAndCollideCamera(aNewPos: vec3, aNewTarget: vec3) {
        let currCamPos = this._camera.getCameraPosition();

        // make local copy of the input arguments
        vec3.copy(this._pgc.fwdCamPos, aNewPos);
        vec3.copy(this._pgc.fwdCamTgt, aNewTarget);
        vec3.subtract(this._pgc.viewDir, aNewTarget, aNewPos);

        // set the new position to be in the xz plane
        this._pgc.fwdCamPos[1] = currCamPos[1];
        this._pgc.fwdCamTgt[1] = currCamPos[1];

        // pick closest point forward in the view direction but in xz plane (independent on pitch)
        // pickedPositionsFwd[1] is picked point some pixels above center of screen (look to pickForCameraCollisionForward function to details)
        // pickedPositionsFwd[0] is picked point in center of screen
        let pointsFwd = this.pickForCameraCollisionForward(aNewPos);

        if (pointsFwd[1]) {
            let distToFwdPickedPoint = vec3.distance(currCamPos, pointsFwd[1]);
            let distToNewPosition = vec3.distance(currCamPos, aNewPos);
            this._autoMovement.distance = distToFwdPickedPoint;
            // logInfo('macc ' + distToFwdPickedPoint);

            let minDistToObstacle = this.avatarWidth + this._collisionCameraForward.getNearValue();
            if ((distToFwdPickedPoint - minDistToObstacle) < distToNewPosition) {
                // compute camera move direction
                vec3.subtract(this._pgc.moveDir, aNewPos, currCamPos);
                vec3.normalize(this._pgc.moveDir, this._pgc.moveDir);

                // compute angle between the two picked points one above other
                if (pointsFwd[0] && pointsFwd[1]) {

                    vec3.subtract(this._pgc.obstacleDir, pointsFwd[1], pointsFwd[0]);
                    vec3.normalize(this._pgc.obstacleDir, this._pgc.obstacleDir);

                    vec3.set(this._pgc.obstacleDir2D, this._pgc.obstacleDir[0], 0.0, this._pgc.obstacleDir[2]);
                    let obstacleMotionAngle = vec3.dot(this._pgc.obstacleDir, this._pgc.obstacleDir2D);
                    if (obstacleMotionAngle < 0.15) {
                        return;
                    }
                }

                // here we are close to some obstacle which will be passed over
                // so we take the picked point which is closer then the aNewPos and use it as new new position
                vec3.copy(this._pgc.fwdCamPos, pointsFwd[0]);

                this._pgc.moveDir[1] = 0.0;

                // then we move minDistToObstacle step back
                vec3.scaleAndAdd(this._pgc.fwdCamPos, this._pgc.fwdCamPos, this._pgc.moveDir, -minDistToObstacle);

                this._pgc.fwdCamPos[1] += this._collisionCameraDown.getNearValue();
                this._pgc.fwdCamTgt[1] += this._collisionCameraDown.getNearValue();
            }
            else {
                // here we can move to aNewPos camera position, and for picking down we ensure the height above some geometry bellow the camera
                this._pgc.fwdCamPos[1] += this._collisionCameraDown.getNearValue();
                this._pgc.fwdCamTgt[1] += this._collisionCameraDown.getNearValue();
            }
        }
        else if (pointsFwd[0]) {
            // the direct picked point is not empty but the upper point is empty (no obstacle) so we can move forward
            // so we can move to aNewPos camera position, and for picking down we ensure the height above some geometry bellow the camera
            this._pgc.fwdCamPos[1] += this._camera.getNearValue();
            this._pgc.fwdCamTgt[1] += this._camera.getNearValue();

            this._autoMovement.distance = this._camera.getFarValue();
        }

        let pointsDown = this.pickForCameraCollisionDown(this._pgc.fwdCamPos);
        this.fallDownResolver.resolve(pointsDown, this._camera, this._pgc.viewDir, this.avatarHeight);
    }

    // handles an event on the canvas for the pointerlock example
    public onMovePointerLock(e: any) {
        super.onMovePointerLock(e);

        this.rotateCameraTarget(this._view.horizontalPixelsToRad(e.dx), this._view.verticalPixelsToRad(e.dy));

        this._view.viewpointChanged();
    }

    public getRealSpeed(): number {
        return this.speed;
    }

    // Set user defined speed step. <0, SPEED_STEPS_MAX).
    public setSpeed(aStep: number) {
        this.step = Math.max(WalkControls.SPEED_STEPS_MIN, Math.min(aStep, WalkControls.SPEED_STEPS_MAX - 1));
        this.speed = this.speedValues[this.step];

        this.emitSpeed();
    }

    // Set user defined mouse sensitivity. Limits <1, 5> checked.
    public setMouseSensitivity(aValue: number) {
        let va = Math.max(WalkControls.MOUSE_SENSITIVITY_MIN, Math.min(aValue, WalkControls.MOUSE_SENSITIVITY_MAX));
        this.mouseSensitivity = WalkControls.MOUSE_SENSITIVITY_MAX - va + 1;
    }

    // Set user defined avatar height. Limits <0.5, 2> checked.
    public setAvatarHeight(aValue: number) {
        vec3.copy(this._pgc.newPos, this._camera.getCameraPosition());
        vec3.copy(this._pgc.newTgt, this._camera.getCameraTarget());

        this._pgc.newPos[1] -= this.avatarHeight;
        this._pgc.newTgt[1] -= this.avatarHeight;

        let newAvatarHeight = Math.max(WalkControls.AVATAR_HEIGHT_MIN, Math.min(aValue, WalkControls.AVATAR_HEIGHT_MAX));
        this.fallDownResolver.setAvatarHeight(this.avatarHeight, newAvatarHeight);
        this.avatarHeight = newAvatarHeight;

        this.moveAndCollideCamera(this._pgc.newPos, this._pgc.newTgt);
        this._viewMode.scheduleRedraw();
    }

    private _initCameraForWalking(): boolean {
        let pickedpos = this.pickForInitCameraForWalking(this._camera.getCameraPosition());
        if (!pickedpos || isNaN(pickedpos[1])) {
            // when nothing is picked in bellow the avatar at its new position the just leave the motion be free
            logInfo("Cannot init camera position for walking: nothing picked bellow the camera");
            return false;
        }

        pickedpos[1] += this.avatarHeight;

        // look forward in the direction of the camera
        vec3.add(this._pgc.newTgt, pickedpos, this._camera.getCameraDirection());
        this._pgc.newTgt[1] = pickedpos[1];

        this._camera.setCameraPositionAndTarget(pickedpos, this._pgc.newTgt);

        this.fallDownResolver.initialize(pickedpos);
        return true;
    }
}
